import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import firebase from 'firebase/app';
import { AuthService } from './auth.service';
import { CustomerdatabaseService } from './customerdatabase.service';
import { environment } from '../../environments/environment';
import { getOnboardingPartner } from '../partner-onboarding/partner-onboarding';

export interface ArsSearch {
  id: string;
  query: string;
  method: string;
  filters?: any;
  createdAt: Date;
  resultCount?: number;
}

export interface ArsDbQueryParams {
  criteria: string;
  location?: string;
  radius?: number;
  max_results?: number;
}

export interface ArsDbApplicant {
  applicant_id: string;
  cv?: string;
  email?: string;
  firstName?: string;
  originForMatching?: string;
  phoneNumber?: string;
  [key: string]: any;
}

export interface ArsMatchingApplicant extends ArsDbApplicant {
  score: number;
  reason: string;
}

export interface ArsMatchingResponse {
  id: string;
  results: ArsMatchingApplicant[];
}

export interface ArsGetMatchingScoreParams {
  criteria: string;
  applicants?: any[];
}

export interface ArsScheduleMeetingsParams {
  matching_id: string;
  recruiter_email: string;
  applicants?: {
    applicant_id: string;
    email: string;
    phone_number: string;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class ArsService {
  private searches$ = new BehaviorSubject<ArsSearch[]>([]);
  private loading$ = new BehaviorSubject<boolean>(false);
  private readonly API_BASE = environment.CLOUD_URL_BASE;

  // A hung scoring request previously left the UI spinning with no error and no feedback.
  static readonly SCORING_TIMEOUT_MS = 4 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private customerDB: CustomerdatabaseService
  ) {}

  getSearches(): Observable<ArsSearch[]> {
    return this.searches$.asObservable();
  }

  getLoading(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  private async getHeaders(): Promise<{ [key: string]: string }> {
    const apiKey = await this.customerDB.getApiKey();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    };
  }

  async partner<T = any>(
    partnerId: string,
    action: 'status' | 'notifyAdmin' | 'acceptConsent' | 'connect' | 'activate' | 'disconnect',
    body: any = {}
  ): Promise<T> {
    const headers = await this.getHeaders();
    const response: any = await this.http.post(
      `${this.API_BASE}/ars/partner/${action}`,
      { ...body, partner: partnerId },
      { headers }
    ).toPromise();
    if (!response?.success) {
      throw new Error(response?.message || `Partner ${action} failed`);
    }
    return response.data as T;
  }

  async savePartnerIntegration(app: any): Promise<void> {
    const partner = getOnboardingPartner(app.appId);
    if (!partner) throw new Error(`Unknown partner: ${app.appId}`);
    if (app.active === false) {
      await this.partner(partner.id, 'disconnect');
      await this.customerDB.saveIntegrationData(app);
      return;
    }
    await this.partner(partner.id, 'connect', {
      url: app[partner.fields.url],
      username: app[partner.fields.username],
      password: app[partner.fields.password] || '',
      statusToPull: app[partner.fields.statusToPull]
    });
    app[partner.fields.password] = '';
    await this.customerDB.saveIntegrationData(app);
    await this.partner(partner.id, 'activate');
  }

  async getApplicantById(id: string, projectId?: string): Promise<ArsDbApplicant | null> {
    if (!id) return null;
    try {
      const headers = await this.getHeaders();
      const payload: any = {
        id,
        from_general_pool: false
      };
      if (projectId) {
        payload.project_id = projectId;
      }
      console.log('[ARS] getApplicantById request', { id, projectId: projectId || null });
      const response: any = await this.http.post(
        `${this.API_BASE}/ars/getApplicantById`,
        payload,
        { headers }
      ).pipe(timeout(ArsService.SCORING_TIMEOUT_MS)).toPromise();
      console.log('[ARS] getApplicantById raw response', {
        success: response?.success,
        message: response?.message,
        keys: response?.data ? Object.keys(response.data) : [],
        data: response?.data
      });
      if (!response?.success) {
        return null;
      }
      const data = response.data || null;
      if (!data) return null;
      return {
        ...data,
        applicant_id: data.applicant_id || data.id || id
      };
    } catch (error) {
      console.error('[ARS] getApplicantById error:', error);
      return null;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/dbQuery (customer's own pool)
  // ──────────────────────────────────────────────
  async dbQuery(params: ArsDbQueryParams): Promise<{ applicants: ArsDbApplicant[] }> {
    try {
      const headers = await this.getHeaders();

      const payload: any = { criteria: params.criteria };
      if (params.location) payload.location = params.location;
      if (params.radius) payload.radius = params.radius;
      if (params.max_results) payload.max_results = params.max_results;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/dbQuery`,
        payload,
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'Database query failed');
      }

      return { applicants: response.data?.applicants || [] };
    } catch (error) {
      console.error('[ARS] dbQuery error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/dbQueryGeneralPool (shared pool)
  // ──────────────────────────────────────────────
  async dbQueryGeneralPool(params: ArsDbQueryParams): Promise<{ applicants: ArsDbApplicant[] }> {
    try {
      const headers = await this.getHeaders();

      const payload: any = { criteria: params.criteria };
      if (params.location) payload.location = params.location;
      if (params.radius) payload.radius = params.radius;
      if (params.max_results) payload.max_results = params.max_results;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/dbQueryGeneralPool`,
        payload,
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'General pool database query failed');
      }

      return { applicants: response.data?.applicants || [] };
    } catch (error) {
      console.error('[ARS] dbQueryGeneralPool error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/matching (customer's own pool, with LLM scoring)
  // ──────────────────────────────────────────────
  async matching(params: ArsDbQueryParams): Promise<ArsMatchingResponse> {
    try {
      const headers = await this.getHeaders();

      const payload: any = { criteria: params.criteria };
      if (params.location) payload.location = params.location;
      if (params.radius) payload.radius = params.radius;
      if (params.max_results) payload.max_results = params.max_results;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/matching`,
        payload,
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'Matching failed');
      }

      return {
        id: response.data?.id || '',
        results: response.data?.results || []
      };
    } catch (error) {
      console.error('[ARS] matching error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/matchingGeneralPool (shared pool, with LLM scoring)
  // ──────────────────────────────────────────────
  async matchingGeneralPool(params: ArsDbQueryParams): Promise<ArsMatchingResponse> {
    try {
      const headers = await this.getHeaders();

      const payload: any = { criteria: params.criteria };
      if (params.location) payload.location = params.location;
      if (params.radius) payload.radius = params.radius;
      if (params.max_results) payload.max_results = params.max_results;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/matchingGeneralPool`,
        payload,
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'General pool matching failed');
      }

      return {
        id: response.data?.id || '',
        results: response.data?.results || []
      };
    } catch (error) {
      console.error('[ARS] matchingGeneralPool error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/refinePrompt
  // ──────────────────────────────────────────────
  async refinePrompt(prompt: string): Promise<any> {
    try {
      const headers = await this.getHeaders();

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/refinePrompt`,
        { prompt },
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'Refine prompt failed');
      }

      return response.data || {};
    } catch (error) {
      console.error('[ARS] refinePrompt error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/getMatchingScore
  // ──────────────────────────────────────────────
  async getMatchingScore(params: ArsGetMatchingScoreParams): Promise<any> {
    try {
      const headers = await this.getHeaders();

      const payload: any = { criteria: params.criteria };
      if (params.applicants) payload.applicants = params.applicants;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/getMatchingScore`,
        payload,
        { headers }
      ).pipe(timeout(ArsService.SCORING_TIMEOUT_MS)).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'Get matching score failed');
      }

      return response.data || {};
    } catch (error) {
      console.error('[ARS] getMatchingScore error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // POST /ars/scheduleMeetings
  // ──────────────────────────────────────────────
  async scheduleMeetings(params: ArsScheduleMeetingsParams): Promise<any> {
    try {
      const headers = await this.getHeaders();

      const payload: any = {
        matching_id: params.matching_id,
        recruiter_email: params.recruiter_email
      };
      if (params.applicants) payload.applicants = params.applicants;

      const response: any = await this.http.post(
        `${this.API_BASE}/ars/scheduleMeetings`,
        payload,
        { headers }
      ).toPromise();

      if (!response?.success) {
        throw new Error(response?.message || 'Schedule meetings failed');
      }

      return response.data || {};
    } catch (error) {
      console.error('[ARS] scheduleMeetings error:', error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────
  // Search history (customer database via app2)
  // ──────────────────────────────────────────────
  private getCustomerFirestore(): firebase.firestore.Firestore | null {
    if (!this.customerDB.app2) return null;
    return this.customerDB.app2.firestore;
  }

  async loadSearches(): Promise<ArsSearch[]> {
    try {
      const db = this.getCustomerFirestore();
      if (!db) return [];

      const searches: ArsSearch[] = [];
      const seenIds = new Set<string>();

      const uid = this.authService.returnUserId();
      if (uid) {
        try {
          const historySnap = await db
            .collection('ars_chat_history')
            .doc(uid)
            .collection('sessions')
            .orderBy('last_active', 'desc')
            .limit(20)
            .get();

          historySnap.forEach((doc: any) => {
            const data = doc.data();
            const chatHistory: any[] = Array.isArray(data.chat_history) ? data.chat_history : [];
            if (chatHistory.length === 0) return;
            
            let query = data.title;
            if (!query || query === 'Chat Session') {
              const firstUserMsg = chatHistory[0]?.user || '';
              query = firstUserMsg
                .replace(/^\[System:.*?\]\s*/i, '')
                .replace(/^\[Mode:.*?\]\s*/i, '')
                .trim()
                .substring(0, 100) || 'Chat Session';
            }
            
            if (query && !seenIds.has(doc.id)) {
              seenIds.add(doc.id);
              searches.push({
                id: doc.id,
                query,
                method: 'chat',
                createdAt: data.last_active?.toDate() || new Date(),
                resultCount: chatHistory.length
              });
            }
          });
        } catch (e) {
          console.warn('[ARS] Error loading chat history sessions:', e);
        }
      }

      try {
        const accountSnap = await this.customerDB.getArsChatSessionsCollection?.()
          ?.orderBy('updated', 'desc')
          .limit(20)
          .get();

        if (accountSnap && !accountSnap.empty) {
          accountSnap.forEach((doc: any) => {
            if (seenIds.has(doc.id)) return;
            const data = doc.data();
            const title = data.title && data.title !== 'Chat Session' ? data.title : '';
            if (title) {
              seenIds.add(doc.id);
              searches.push({
                id: doc.id,
                query: title.substring(0, 100),
                method: 'chat',
                createdAt: data.updated?.toDate() || data.created?.toDate() || new Date(),
                resultCount: data.messageCount || 0
              });
            }
          });
        }
      } catch (e) {
        console.warn('[ARS] Error loading account sessions:', e);
      }

      try {
        const legacySnap = await db
          .collection('ars_searches')
          .orderBy('createdAt', 'desc')
          .limit(10)
          .get();

        legacySnap.forEach((doc: any) => {
          if (seenIds.has(doc.id)) return;
          const data = doc.data();
          if (data.query) {
            seenIds.add(doc.id);
            searches.push({
              id: doc.id,
              query: data.query || '',
              method: data.method || 'text',
              filters: data.filters || null,
              createdAt: data.createdAt?.toDate() || new Date(),
              resultCount: data.resultCount || 0
            });
          }
        });
      } catch (e) {
        console.warn('[ARS] Error loading legacy searches:', e);
      }

      searches.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      const topSearches = searches.slice(0, 20);

      this.searches$.next(topSearches);
      return topSearches;
    } catch (error) {
      console.error('[ARS] Error loading searches:', error);
      return [];
    }
  }

  private async saveSearch(query: string, method: string, filters?: any, resultCount?: number): Promise<ArsSearch> {
    const db = this.getCustomerFirestore();
    if (!db) throw new Error('Customer database not connected');

    const searchData: any = {
      query: query || '',
      method,
      resultCount: resultCount || 0,
      createdAt: firebase.firestore.FieldValue.serverTimestamp()
    };
    if (filters) searchData.filters = filters;

    const docRef = await db
      .collection('ars_searches')
      .add(searchData);

    const search: ArsSearch = {
      id: docRef.id,
      query: query || '',
      method,
      filters,
      createdAt: new Date(),
      resultCount: resultCount || 0
    };

    const current = this.searches$.value;
    this.searches$.next([search, ...current].slice(0, 20));

    return search;
  }

  async deleteSearch(searchId: string): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    await db
      .collection('ars_searches')
      .doc(searchId)
      .delete();

    const current = this.searches$.value;
    this.searches$.next(current.filter(s => s.id !== searchId));
  }
}
